import { openDB } from 'idb';
import { db, auth } from './firebase'; // Importa Firestore e auth
import { getStorage, ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { setDoc, doc, deleteDoc } from 'firebase/firestore';


// Costanti per IndexedDB
const DB_NAME = 'AnnotationDB';
const DB_VERSION = 8; // Versione incrementata per le modifiche
const STORE_NAME = 'annotations';

// Imposta il riferimento a Firebase Storage
const storage = getStorage();

// Funzione di inizializzazione del database IndexedDB
export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(idb, oldVersion, newVersion, transaction) {
      let store;
      if (!idb.objectStoreNames.contains(STORE_NAME)) {
        store = idb.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      } else {
        store = transaction.objectStore(STORE_NAME);
      }

      // Creazione degli indici se non esistono
      if (!store.indexNames.contains('by_id')) {
        store.createIndex('by_id', 'id');
      }
      if (!store.indexNames.contains('by_image_name')) {
        store.createIndex('by_image_name', 'subImage.name');
      }
      if (!store.indexNames.contains('by_email')) {
        store.createIndex('by_email', 'email');
      }
      if (!store.indexNames.contains('by_tool')) {
        store.createIndex('by_tool', 'tool');
      }
      if (!store.indexNames.contains('by_original_image_name')) {
        store.createIndex('by_original_image_name', 'subImage.originalImageName');
      }
    },
  });
};

// Funzione per salvare una sessione nel database
export const saveSession = async (sessionData) => {
  if (!auth.currentUser) {
    console.error('Utente non autenticato');
    return;
  }
  // Verifica della presenza di subImage oppure dataURL
  if (sessionData.subImage) {
    if (!sessionData.subImage.dataURL && sessionData.tool !== 'original-image') {
      console.error('Tentativo di salvare una sessione senza subImage.dataURL:', sessionData);
      return;
    }
  } else if (!sessionData.dataURL) {
    console.error('Tentativo di salvare una sessione senza dataURL:', sessionData);
    return;
  }

  if (!sessionData.email) {
    console.error('Tentativo di salvare una sessione senza email:', sessionData);
    return;
  }

  try {
    console.log('Salvataggio sessione:', sessionData);
    const idbInstance = await initDB();
    const tx = idbInstance.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const indexByEmail = store.index('by_email');

    // Recupera tutte le sessioni dell'utente corrente
    const userSessions = await indexByEmail.getAll(sessionData.email);

    // Determina il nome immagine da usare per il confronto
    let imageName = sessionData.subImage ? sessionData.subImage.name : sessionData.name;

    // Verifica se esiste già una sessione con lo stesso nome immagine
    const existingSessions = userSessions.filter(
      (session) => session.subImage?.name === imageName || session.name === imageName
    );

    if (existingSessions.length > 0) {
      // Aggiorna la sessione esistente
      const existingSession = existingSessions[0];
      await store.put({ ...existingSession, ...sessionData });
    } else {
      // Aggiunge una nuova sessione (l'ID viene gestito automaticamente)
      await store.add(sessionData);
    }

    await tx.done;

    // Aggiunge il campo userId ai dati della sessione
    const userId = auth.currentUser.uid;
    sessionData.userId = userId;

    // Prepara l'immagine da caricare su Firebase Storage
    let subImageForStorage = sessionData.subImage
      ? sessionData.subImage
      : { name: sessionData.name, dataURL: sessionData.dataURL };

    let downloadURL;

    // Se l'immagine ha già un downloadURL, non serve ri-caricarla su Firebase Storage
    if (subImageForStorage.downloadURL) {
      downloadURL = subImageForStorage.downloadURL;
    } else if (subImageForStorage.dataURL) {
      // Carica l'immagine su Firebase Storage solo se ha un dataURL e non ha ancora downloadURL
      const storageRef = ref(storage, `images/${userId}/${subImageForStorage.name}`);
      await uploadString(storageRef, subImageForStorage.dataURL, 'data_url');
      downloadURL = await getDownloadURL(storageRef);
    } else {
      // Se non c'è né dataURL né downloadURL, non possiamo procedere con l'upload
      console.warn('Nessuna immagine da caricare su Firebase Storage');
      downloadURL = null;
    }

    // Prepara i dati per Firestore: rimuove il dataURL e aggiunge downloadURL
    const sessionDataForFirestore = {
      ...sessionData,
      subImage: {
        ...subImageForStorage,
        dataURL: null, // rimuove il dataURL
        downloadURL,   // aggiunge l'URL di download
        isExcluded: sessionData.isExcluded || false,
      },
    };

    // Salva su Firestore con merge
    const sessionDocRef = doc(db, 'sessions', `${userId}-${subImageForStorage.name}`);
    await setDoc(sessionDocRef, sessionDataForFirestore, { merge: true });

    console.log('Sessione salvata correttamente.');
  } catch (error) {
    console.error('Errore nel salvare la sessione:', error);
  }
};

// Recupera tutte le sessioni per un determinato utente. Se l'utente è superadmin,
// restituisce tutti i documenti.
export const getAllSessions = async (userEmail) => {
  if (!userEmail) {
    console.error('getAllSessions richiede userEmail come parametro');
    return [];
  }

  try {
    const idbInstance = await initDB();
    const tx = idbInstance.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    let sessions;
    if (userEmail === "dentaidenai6@gmail.com") {
      sessions = await store.getAll();
    } else {
      const indexByEmail = store.index('by_email');
      sessions = await indexByEmail.getAll(userEmail);
    }
    await tx.done;
    return sessions;
  } catch (error) {
    console.error('Errore nel recuperare le sessioni:', error);
    return [];
  }
};

// Recupera una sessione per ID
export const getSessionById = async (id) => {
  try {
    const idbInstance = await initDB();
    return await idbInstance.get(STORE_NAME, id);
  } catch (error) {
    console.error('Errore nel recuperare la sessione per ID:', error);
    return null;
  }
};

// Funzione per eliminare una sessione:
// 1. Recupera tutte le sessioni da IndexedDB corrispondenti al nome immagine.
// 2. Per ciascuna sessione, elimina il file su Firebase Storage e il documento su Firestore.
// 3. Infine, elimina il record da IndexedDB usando una nuova transazione per ogni cancellazione.
export const deleteSession = async (imageName, userEmail) => {
  if (!userEmail) {
    console.error('deleteSession richiede userEmail come parametro');
    return;
  }

  // Verifica che l'utente sia autenticato
  if (!auth.currentUser) {
    console.error('Utente non autenticato');
    throw new Error('Utente non autenticato');
  }

  // Ottieni l'ID dell'utente autenticato
  const currentUserId = auth.currentUser.uid;
  console.log('Utente autenticato:', currentUserId, 'Email:', userEmail);

  try {
    const idbInstance = await initDB();

    // Recupera tutte le sessioni in una singola transazione
    const tx = idbInstance.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    // Recupera le sessioni con il nome immagine specificato
    const indexByImageName = store.index('by_image_name');
    const sessionsByImageName = await indexByImageName.getAll(imageName);
    console.log('Sessioni trovate per nome immagine:', sessionsByImageName.length);

    // Recupera tutte le sotto-immagini associate all'immagine originale
    let subImages = [];
    try {
      // Verifica se l'indice esiste prima di usarlo
      if (store.indexNames.contains('by_original_image_name')) {
        const indexByOriginalImageName = store.index('by_original_image_name');
        subImages = await indexByOriginalImageName.getAll(imageName);
        console.log('Sotto-immagini trovate per immagine originale:', subImages.length);
      } else {
        console.warn('Indice by_original_image_name non trovato, cercherò le sotto-immagini manualmente');
        // Recupera tutte le sessioni e filtra manualmente
        const allSessions = await store.getAll();
        subImages = allSessions.filter(session => 
          session.subImage && 
          session.subImage.originalImageName === imageName
        );
        console.log('Sotto-immagini trovate manualmente:', subImages.length);
      }
    } catch (error) {
      console.warn('Errore nel recupero delle sotto-immagini:', error);
      // Continua con un array vuoto
    }
    
    // Completa la transazione dopo aver recuperato tutti i dati necessari
    await tx.done;

    // Filtra le sessioni in base all'email
    const sessionsToDelete = (userEmail === "dentaidenai6@gmail.com")
      ? sessionsByImageName
      : sessionsByImageName.filter(session => session.email === userEmail);
    console.log('Sessioni da eliminare dopo filtro email:', sessionsToDelete.length);

    // Filtra le sotto-immagini in base all'email
    const subImagesToDelete = (userEmail === "dentaidenai6@gmail.com")
      ? subImages
      : subImages.filter(session => session.email === userEmail);
    console.log('Sotto-immagini da eliminare dopo filtro email:', subImagesToDelete.length);

    // Funzione per eliminare un file da Firebase Storage
    const deleteFileFromStorage = async (userId, fileName) => {
      const fileRef = ref(storage, `images/${userId}/${fileName}`);
      try {
        await deleteObject(fileRef);
        console.log(`Immagine eliminata da Firebase Storage: images/${userId}/${fileName}`);
        return true;
      } catch (err) {
        console.warn(`Errore nell'eliminazione del file su Firebase Storage: images/${userId}/${fileName}`, err);
        return false;
      }
    };

    // Funzione per eliminare un documento da Firestore
    const deleteDocFromFirestore = async (docId) => {
      const sessionDocRef = doc(db, 'sessions', docId);
      try {
        await deleteDoc(sessionDocRef);
        console.log(`Documento Firestore eliminato: ${docId}`);
        return true;
      } catch (err) {
        console.error(`Errore nell'eliminazione del documento Firestore: ${docId}`, err);
        return false;
      }
    };

    // Funzione per eliminare un record da IndexedDB
    const deleteRecordFromIndexedDB = async (id) => {
      if (!id) return false;
      try {
        const deleteTx = idbInstance.transaction(STORE_NAME, 'readwrite');
        await deleteTx.objectStore(STORE_NAME).delete(id);
        await deleteTx.done;
        console.log(`Record IndexedDB eliminato: ${id}`);
        return true;
      } catch (err) {
        console.error(`Errore nell'eliminazione del record IndexedDB: ${id}`, err);
        return false;
      }
    };

    // Elimina le sotto-immagini
    for (const subImage of subImagesToDelete) {
      try {
        if (!subImage || !subImage.subImage || !subImage.subImage.name) {
          console.error('Sotto-immagine non valida:', subImage);
          continue;
        }

        console.log('Elaborazione sotto-immagine:', subImage.id, subImage.subImage.name);

        // Usa l'ID dell'utente corrente o quello della sessione
        const userId = subImage.userId || currentUserId;
        const fileName = subImage.subImage.name;
        const docId = `${userId}-${fileName}`;

        // Elimina il file da Firebase Storage
        await deleteFileFromStorage(userId, fileName);

        // Elimina il documento da Firestore
        await deleteDocFromFirestore(docId);

        // Elimina il record da IndexedDB
        await deleteRecordFromIndexedDB(subImage.id);
      } catch (error) {
        console.error('Errore durante l\'eliminazione della sotto-immagine:', error);
      }
    }

    // Elimina le sessioni principali
    for (const session of sessionsToDelete) {
      try {
        // Verifica se la sessione ha i dati necessari
        if (!session) {
          console.error('Sessione non valida: sessione vuota');
          continue;
        }

        console.log('Elaborazione sessione principale:', session.id, session.subImage?.name);

        // Se la sessione non ha subImage, crea un oggetto subImage minimo
        if (!session.subImage) {
          session.subImage = { name: imageName };
        }

        // Usa l'ID dell'utente corrente o quello della sessione
        const userId = session.userId || currentUserId;
        const fileName = session.subImage.name;
        const docId = `${userId}-${fileName}`;

        // Elimina il file da Firebase Storage
        await deleteFileFromStorage(userId, fileName);

        // Elimina il documento da Firestore
        await deleteDocFromFirestore(docId);

        // Elimina il record da IndexedDB
        await deleteRecordFromIndexedDB(session.id);
      } catch (error) {
        console.error('Errore durante l\'eliminazione della sessione principale:', error);
      }
    }
  } catch (error) {
    console.error('Errore nella funzione deleteSession:', error);
    throw error; // Rilancia l'errore per gestirlo nel componente
  }
};

// Recupera una sessione in base al nome immagine e all'email
export const getSessionByImageNameAndEmail = async (imageName, userEmail) => {
  if (!userEmail || !imageName) {
    console.error('getSessionByImageNameAndEmail richiede imageName e userEmail come parametri');
    return null;
  }

  try {
    const idbInstance = await initDB();
    const tx = idbInstance.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    let sessions;
    if (userEmail === "dentaidenai6@gmail.com") {
      sessions = await store.getAll();
    } else {
      const indexByEmail = store.index('by_email');
      sessions = await indexByEmail.getAll(userEmail);
    }
    const session = sessions.find((session) => session.subImage && session.subImage.name === imageName);
    await tx.done;
    return session || null;
  } catch (error) {
    console.error('Errore nel recuperare la sessione per nome immagine ed email:', error);
    return null;
  }
};

// Recupera tutte le sotto-immagini per una data immagine originale e tool.
// Se l'utente è superadmin, non filtra per email.
export const getSubImagesByOriginalImageNameAndTool = async (userEmail, originalImageName, tool) => {
  if (!userEmail || !originalImageName || !tool) {
    console.error('getSubImagesByOriginalImageNameAndTool richiede userEmail, originalImageName e tool come parametri');
    return [];
  }

  try {
    const idbInstance = await initDB();
    const tx = idbInstance.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    let sessions;
    if (userEmail === "dentaidenai6@gmail.com") {
      sessions = await store.getAll();
    } else {
      const indexByTool = store.index('by_tool');
      sessions = await indexByTool.getAll(tool);
    }
    // Filtra per originalImageName e, se non è superadmin, per email
    const subImages = sessions.filter(session => 
      session.subImage &&
      session.subImage.originalImageName === originalImageName &&
      (userEmail === "dentaidenai6@gmail.com" || session.email === userEmail)
    );
    await tx.done;
    return subImages;
  } catch (error) {
    console.error('Errore nel recuperare le sotto-immagini:', error);
    return [];
  }
};

// Recupera tutte le sessioni per email e tool specificato.
// Se l'utente è superadmin, restituisce tutte le sessioni filtrate per tool.
export const getSessionsByEmailAndTool = async (userEmail, tool) => {
  if (!userEmail) {
    console.error('getSessionsByEmailAndTool richiede userEmail come parametro');
    return [];
  }
  if (!tool) {
    console.error('getSessionsByEmailAndTool richiede tool come parametro');
    return [];
  }

  try {
    const idbInstance = await initDB();
    const tx = idbInstance.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    let sessions;
    if (userEmail === "dentaidenai6@gmail.com") {
      sessions = await store.getAll();
    } else {
      const indexByEmail = store.index('by_email');
      sessions = await indexByEmail.getAll(userEmail);
    }
    const filteredSessions = sessions.filter(session => session.tool === tool);
    await tx.done;
    return filteredSessions;
  } catch (error) {
    console.error('Errore nel recuperare le sessioni:', error);
    return [];
  }
};

// Recupera una sessione in base ai dettagli della sotto-immagine.
// Se l'utente è superadmin, controlla tra tutte le sessioni.
export const getSessionBySubImageDetails = async (subImageName, originalImageName, userEmail) => {
  if (!userEmail || !subImageName || !originalImageName) {
    console.error('getSessionBySubImageDetails richiede subImageName, originalImageName e userEmail come parametri');
    return null;
  }

  try {
    const idbInstance = await initDB();
    const tx = idbInstance.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    let sessions;
    if (userEmail === "dentaidenai6@gmail.com") {
      sessions = await store.getAll();
    } else {
      const indexByEmail = store.index('by_email');
      sessions = await indexByEmail.getAll(userEmail);
    }
    const session = sessions.find((session) =>
      session.subImage &&
      session.subImage.name === subImageName &&
      session.subImage.originalImageName === originalImageName
    );
    await tx.done;
    return session || null;
  } catch (error) {
    console.error('Errore nel recuperare la sessione per dettagli della sotto-immagine:', error);
    return null;
  }
};
